import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { aiAPI } from "@/lib/api";
import { Bot, X, Send, Loader2, Sparkles, Trash2 } from "lucide-react";

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const history = messages.slice(-6).map(m => ({ role: m.role, content: m.content }));
      const r = await aiAPI.chat(text, history);
      setMessages(prev => [...prev, { role: "assistant", content: r.data.response }]);
    } catch (e) {
      const errMsg = e?.response?.data?.detail || "Erreur de communication avec l'IA";
      setMessages(prev => [...prev, { role: "assistant", content: errMsg, error: true }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => setMessages([]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="hidden lg:flex fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all items-center justify-center group"
        data-testid="ai-chat-toggle"
      >
        <Bot size={24} className="group-hover:scale-110 transition-transform" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
      </button>
    );
  }

  return (
    <div className="hidden lg:flex fixed bottom-20 right-6 z-50 w-[400px] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex-col overflow-hidden" data-testid="ai-chat-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={18} />
          <span className="font-semibold text-sm">Assistant IA</span>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button onClick={clearChat} className="p-1.5 rounded-lg hover:bg-white/20 transition" title="Effacer"><Trash2 size={14} /></button>
          )}
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition" data-testid="ai-chat-close"><X size={16} /></button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Bot size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">Comment puis-je vous aider ?</p>
            <p className="text-xs text-slate-400 mt-1">Posez-moi des questions sur vos clients, factures, produits...</p>
            <div className="flex flex-wrap gap-1.5 justify-center mt-4">
              {["Quel est mon CA total ?", "Factures impayees ?", "Top clients ?"].map(q => (
                <button key={q} onClick={() => { setInput(q); }} className="text-[11px] px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 transition" data-testid={`suggestion-${q.slice(0,10)}`}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={`${msg.role}-${i}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
              msg.role === "user"
                ? "bg-indigo-600 text-white rounded-br-sm"
                : msg.error
                  ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                  : "bg-slate-100 text-slate-700 rounded-bl-sm"
            }`}>
              <div className="whitespace-pre-line">{msg.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 px-3 py-2 rounded-xl rounded-bl-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t px-3 py-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Posez une question..."
            className="flex-1 text-sm h-9 px-3 rounded-lg border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none"
            disabled={loading}
            data-testid="ai-chat-input"
          />
          <Button size="icon" className="h-9 w-9 bg-indigo-600 hover:bg-indigo-700 rounded-lg shrink-0" onClick={sendMessage} disabled={loading || !input.trim()} data-testid="ai-chat-send">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </div>
      </div>
    </div>
  );
}
